"use client";

import { Ref, forwardRef, useState, useEffect, useMemo } from "react";
import Image, { ImageProps } from "next/image";
import { motion, useReducedMotion, type Variants } from "framer-motion";

import { cn } from "@/lib/utils";
import Link from "next/link";
import { FlipWords } from "@/components/ui/flip-words";

export const PhotoGallery = ({
  animationDelay = 0.5,
}: {
  animationDelay?: number;
}) => {
  const prefersReducedMotion = useReducedMotion();
  const [isVisible, setIsVisible] = useState(true); // Start visible immediately
  const [isLoaded, setIsLoaded] = useState(true); // Start loaded immediately
  const [isHydrated, setIsHydrated] = useState(false);

  // Defer starting animations until after hydration + a couple rafs to avoid
  // SSR hydration jank causing a visible jump or skipped frames.
  useEffect(() => {
    let raf1: number | null = null;
    let raf2: number | null = null;
    const start = () => setIsHydrated(true);
    if (typeof window !== 'undefined') {
      raf1 = window.requestAnimationFrame(() => {
        raf2 = window.requestAnimationFrame(start);
      });
    } else {
      setIsHydrated(true);
    }
    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, []);

  // Words for the flip effect
  const flipWords = ["NATURAL CONNECTIONS", "REAL RELATIONSHIPS", "AUTHENTIC BONDS", "GENUINE NETWORKS"];

  useEffect(() => {
    if (prefersReducedMotion) {
      setIsVisible(true);
      setIsLoaded(true);
      return;
    }
    const visibilityTimer = setTimeout(() => setIsVisible(true), animationDelay * 1000);
    const animationTimer = setTimeout(() => setIsLoaded(true), (animationDelay + 0.1) * 1000);
    return () => {
      clearTimeout(visibilityTimer);
      clearTimeout(animationTimer);
    };
  }, [animationDelay, prefersReducedMotion]);

  // Animation variants for the container
  const containerVariants = prefersReducedMotion
    ? undefined
    : {
        hidden: { opacity: 1 },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: 0.05,
            delayChildren: 0,
          },
        },
      } as const;

  // Animation variants for each photo
  const photoVariants: Variants | undefined = prefersReducedMotion
    ? undefined
    : {
        hidden: () => ({ x: 0, y: 0, rotate: 0, scale: 1 }),
        visible: (custom: { x: any; y: any; order: number; rotate?: number }) => ({
          x: custom.x,
          y: custom.y,
          rotate: typeof custom.rotate === 'number' ? custom.rotate : 0,
          scale: 1,
          transition: {
            type: "spring",
            stiffness: 60,
            damping: 16,
            mass: 0.9,
            delay: custom.order * 0.12,
          },
        }),
      } as const;

  // Photo positions - horizontal layout with random y offsets
  const photos = useMemo(() => ([
    {
      id: 1,
      order: 0,
      x: "-600px",
      y: "15px",
      zIndex: 50, // Highest z-index (on top)
      direction: "left" as Direction,
      src: "https://images.pexels.com/photos/1181406/pexels-photo-1181406.jpeg",
      alt: "Two colleagues collaborating at a laptop in a modern workspace",
      baseRotate: -1,
    },
    {
      id: 2,
      order: 1,
      x: "-300px",
      y: "32px",
      zIndex: 40,
      direction: "left" as Direction,
      src: "https://images.pexels.com/photos/1181263/pexels-photo-1181263.jpeg",
      alt: "Group brainstorming with sticky notes and tablets",
      baseRotate: 0.8,
    },
    {
      id: 3,
      order: 2,
      x: "0px",
      y: "8px",
      zIndex: 30,
      direction: "right" as Direction,
      src: "https://images.pexels.com/photos/1181396/pexels-photo-1181396.jpeg",
      alt: "Handshake between professionals at a networking event",
      baseRotate: 0.2,
    },
    {
      id: 4,
      order: 3,
      x: "300px",
      y: "22px",
      zIndex: 10,
      direction: "right" as Direction,
      src: "https://images.pexels.com/photos/1181467/pexels-photo-1181467.jpeg",
      alt: "Smiling team discussing strategy around a tablet",
      baseRotate: -0.9,
    },
    {
      id: 5,
      order: 4,
      x: "600px",
      y: "44px",
      zIndex: 10, // Lowest z-index (at bottom)
      direction: "left" as Direction,
      src: "https://images.pexels.com/photos/1181316/pexels-photo-1181316.jpeg",
      alt: "Coworkers sharing ideas during an informal meeting",
      baseRotate: 1.1,
    },
  ]), []);

  // Deterministic pseudo-random rotation per card for desktop (SSR-safe)
  const desktopRotationById = useMemo(() => {
    const seededRandom = (seed: number) => {
      // Simple deterministic PRNG based on sine; returns [0,1)
      const x = Math.sin(seed * 9301 + 49297) * 233280;
      return x - Math.floor(x);
    };
    const map = new Map<number, number>();
    photos.forEach((p) => {
      const r = seededRandom(p.id); // [0,1)
      let angle = r * 10 - 5; // -5..+5 deg
      if (Math.abs(angle) < 1) angle = angle < 0 ? -1 : 1; // avoid ~0deg
      map.set(p.id, parseFloat(angle.toFixed(2)));
    });
    return map;
  }, [photos]);

  return (
    <div className="mt-0 mb-0 relative h-screen flex flex-col items-center justify-between p-2 sm:p-4 lg:p-0 pt-8 sm:pt-12 lg:pt-12 pb-4 sm:pb-6">
      <div className="max-w-6xl mx-auto w-full flex-1 flex flex-col justify-center min-h-0">
        {/* Mobile Text Layout */}
        <div className="block lg:hidden text-center mb-4 sm:mb-6 px-2">
        <motion.div
          className="bg-[#facc15] border-2 border-black px-3 py-2 inline-block transform -rotate-1 mb-4"
          initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
          animate={{ opacity: 1, scale: 1, rotate: -1 }}
          transition={{ duration: 0.3, delay: 0, ease: "easeOut" }}
        >
          <p className="text-xs xs:text-sm sm:text-base font-black uppercase tracking-tight text-black">
            REAL EVENTS, REAL CONNECTIONS
          </p>
        </motion.div>

        <motion.div
          className="space-y-2 sm:space-y-4 max-w-xs sm:max-w-sm mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
        >
          <div className="bg-[#17cbbf] border-2 sm:border-4 border-black p-3 sm:p-4 inline-block transform rotate-1 z-10 ">
            <h1 className="text-base xs:text-lg sm:text-xl md:text-2xl font-black text-black leading-tight whitespace-nowrap">
              TRANSFORM NETWORKING INTO
            </h1>
          </div>

          <motion.div
            className="bg-[#1e74e6] border-2 sm:border-4 border-black p-3 sm:p-4 inline-block transform -rotate-2 z-20"
            initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
            animate={{ opacity: 1, scale: 1, rotate: -2 }}
            transition={{ duration: 1.2, delay: 0.8, ease: "easeOut" }}
          >
            <div className="text-base xs:text-lg sm:text-xl md:text-2xl font-black text-black text-center z-20" style={{ width: '280px', zIndex:20}}>
              <FlipWords
                words={flipWords}
                duration={3000}
                className="font-black text-black whitespace-nowrap z-20"
              />
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Desktop Text Layout */}
      <div className="hidden lg:block text-center mb-8 px-4">
        <motion.div
          className="bg-[#facc15] border-4 border-black p-3 inline-block transform -rotate-1 mb-6"
          initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
          animate={{ opacity: 1, scale: 1, rotate: -1 }}
          transition={{ duration: 0.3, delay: 0, ease: "easeOut" }}
        >
          <p className="text-sm font-black uppercase tracking-wider text-black">
            REAL EVENTS, REAL CONNECTIONS
          </p>
        </motion.div>

        <motion.div
          className="space-y-6 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
        >
          <div className="bg-[#17cbbf] border-4 border-black p-4 lg:p-6 inline-block transform rotate-1">
            <h1 className="text-2xl lg:text-3xl xl:text-4xl font-black text-black leading-tight whitespace-nowrap">
              TRANSFORM NETWORKING INTO
            </h1>
          </div>

          <motion.div
            className="bg-[#1e74e6] border-4 border-black p-4 lg:p-6 inline-block transform -rotate-2"
            initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
            animate={{ opacity: 1, scale: 1, rotate: -2 }}
            transition={{ duration: 1.2, delay: 0.8, ease: "easeOut" }}
          >
            <div className="text-2xl lg:text-3xl xl:text-4xl font-black text-black text-center z-10" style={{ width: '450px' }}>
              <FlipWords
                words={flipWords}
                duration={3000}
                className="font-black text-black whitespace-nowrap"
              />
            </div>
          </motion.div>
        </motion.div>
      </div>
      {/* Mobile Grid Layout */}
      <div className="block lg:hidden relative w-full mb-4 px-2">
        <motion.div
          className="relative mx-auto flex w-full max-w-lg sm:max-w-xl justify-center"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <motion.div
            className="grid grid-cols-2 gap-4 sm:gap-4 w-full"
            variants={containerVariants}
            initial="hidden"
            animate={isHydrated ? "visible" : "hidden"}
          >
              {photos.slice(0, 4).map((photo, index) => (
              <motion.div
                key={photo.id}
                className={`relative border-2 sm:border-4 border-black shadow-lg ${
                  index % 4 === 0
                    ? "bg-green-400 hover:bg-green-300"
                    : index % 4 === 1
                      ? "bg-purple-400 hover:bg-purple-300"
                      : index % 4 === 2
                        ? "bg-orange-400 hover:bg-orange-300"
                        : "bg-red-400 hover:bg-red-300"
                } transition-colors duration-200`}
                variants={photoVariants}
                custom={{
                  x: 0,
                  y: 0,
                    order: photo.order,
                    rotate: 0,
                }}
                whileHover={{ zIndex: 10, scale: 1.03 }}
              >
                <motion.div
                  className="p-2 sm:p-3 relative"
                  initial={{ rotate: 0 }}
                  animate={{ rotate: [3, -2, 2, -3][index % 4] }}
                  transition={{ type: "spring", stiffness: 70, damping: 12, mass: 1, delay: 0.05 * index }}
                >
                    <Photo
                    width={140}
                    height={140}
                    src={photo.src}
                      alt={photo.alt}
                    direction={photo.direction}
                      priority={index < 2}
                      sizes="(max-width: 640px) 45vw, 25vw"
                  />
                  {/* Add small decorative element */}
                  <div className={`absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-4 h-4 sm:w-6 sm:h-6 border-2 border-black transform rotate-45 ${
                    index % 4 === 0
                      ? "bg-yellow-400"
                      : index % 4 === 1
                        ? "bg-pink-400"
                        : index % 4 === 2
                          ? "bg-blue-400"
                          : "bg-cyan-400"
                  }`}></div>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Desktop Horizontal Layout */}
      <div className="hidden lg:block relative w-full mb-6 px-4">
        <motion.div
          className="relative mx-auto flex w-full max-w-6xl xl:max-w-7xl justify-center overflow-visible"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <motion.div
            className="relative flex justify-center"
            variants={containerVariants}
            initial="hidden"
            animate={isHydrated ? "visible" : "hidden"}
          >
            <div className="relative w-full h-[220px] lg:h-[260px] xl:h-[300px] flex justify-center">
              {[...photos].reverse().map((photo, index) => (
                <motion.div
                  key={photo.id}
                  className={`absolute border-4 border-black shadow-xl ${
                    index % 5 === 0
                      ? "bg-green-400 hover:bg-green-300"
                      : index % 5 === 1
                        ? "bg-purple-400 hover:bg-purple-300"
                        : index % 5 === 2
                          ? "bg-orange-400 hover:bg-orange-300"
                          : index % 5 === 3
                            ? "bg-red-400 hover:bg-red-300"
                            : "bg-yellow-400 hover:bg-yellow-300"
                  } transition-colors duration-200`}
                  style={{
                    zIndex: photo.zIndex,
                  }}
                  variants={photoVariants}
                  custom={{
                    x: photo.x,
                    y: photo.y,
                    order: photo.order,
                    rotate: desktopRotationById.get(photo.id) ?? 0,
                  }}
                  whileHover={{
                    zIndex: 10000,
                    scale: 1.1,
                    y: -10,
                    rotate: (desktopRotationById.get(photo.id) ?? 0) + (photo.direction === 'left' ? -2 : 2),
                  }}
                >
                  <div className="p-4 lg:p-5 relative">
                    <Photo
                      width={200}
                      height={200}
                      src={photo.src}
                      alt={photo.alt}
                      direction={photo.direction}
                      priority={index < 3}
                      sizes="(min-width: 1024px) 18vw, 40vw"
                    />
                    {/* Add decorative corner elements */}
                    <div className={`absolute -top-2 -right-2 lg:-top-3 lg:-right-3 w-6 h-6 lg:w-8 lg:h-8 border-2 lg:border-3 border-black transform rotate-45 ${
                      index % 5 === 0
                        ? "bg-pink-400"
                        : index % 5 === 1
                          ? "bg-blue-400"
                          : index % 5 === 2
                            ? "bg-cyan-400"
                            : index % 5 === 3
                              ? "bg-lime-400"
                              : "bg-rose-400"
                    }`}></div>
                    <div className={`absolute -bottom-2 -left-2 w-5 h-5 lg:w-6 lg:h-6 border-2 border-black rounded-full ${
                      index % 5 === 0
                        ? "bg-blue-400"
                        : index % 5 === 1
                          ? "bg-yellow-400"
                          : index % 5 === 2
                            ? "bg-pink-400"
                            : index % 5 === 3
                              ? "bg-green-400"
                              : "bg-purple-400"
                    }`}></div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
      <motion.div
        className="flex w-full justify-center px-4 flex-shrink-0"
        initial={{ opacity: 0, y: 20, scale: 0.9, rotate: -5 }}
        animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
        transition={{ duration: 0.8, delay: 0.8, ease: "easeOut" }}
      >
        <Link href="/signup">
          <motion.button
            className="bg-[#facc15] border-2 sm:border-4 border-black px-4 py-2 sm:px-6 sm:py-3 font-black text-base sm:text-lg lg:text-xl text-black transform rotate-2 hover:rotate-0 hover:bg-black hover:text-white transition-all duration-200 shadow-[3px_3px_0px_0px_#000000] sm:shadow-[6px_6px_0px_0px_#000000] hover:shadow-[2px_2px_0px_0px_#000000] hover:translate-x-1 hover:translate-y-1"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            START NETWORKING NOW!
          </motion.button>
        </Link>
      </motion.div>
      </div>
    </div>
  );
};

function getRandomNumberInRange(min: number, max: number): number {
  if (min >= max) {
    throw new Error("Min value should be less than max value");
  }
  return Math.random() * (max - min) + min;
}

const MotionImage = motion(
  forwardRef(function MotionImage(
    props: ImageProps & { alt: string },
    ref: Ref<HTMLImageElement>
  ) {
    const { alt, ...restProps } = props;
    return <Image ref={ref} alt={alt} {...restProps} />;
  })
);

type Direction = "left" | "right";

export const Photo = ({
  src,
  alt,
  className,
  direction,
  width,
  height,
  priority,
  sizes,
  ...props
}: {
  src: string;
  alt: string;
  className?: string;
  direction?: Direction;
  width: number;
  height: number;
  priority?: boolean;
  sizes?: string;
}) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const easterEggs = [
    "🎉 You found me!",
    "🚀 Secret discovered!",
    "✨ Easter egg!",
    "🎯 Nice find!",
    "🔍 You're curious!",
    "🎪 Surprise!",
    "🦄 Magical!",
    "🌟 Hidden gem!",
  ];
  // Deterministic selection to avoid SSR/client mismatch
  const eggIndex = useMemo(() => {
    let hash = 0;
    const key = `${src}|${alt}`;
    for (let i = 0; i < key.length; i++) {
      hash = (hash << 5) - hash + key.charCodeAt(i);
      hash |= 0; // 32-bit
    }
    return Math.abs(hash) % easterEggs.length;
  }, [src, alt, easterEggs.length]);
  const randomEgg = easterEggs[eggIndex];

  return (
    <div className="relative">
      {/* Easter Egg Background - toggled with drag state */}
      <div 
        className={cn(
          "absolute inset-0 rounded-3xl border-4 border-dashed border-purple-400 bg-gradient-to-br from-pink-200 via-purple-200 to-cyan-200 flex items-center justify-center z-0 transition-all duration-300",
          isDragging ? "opacity-100 scale-100" : "opacity-0 scale-90"
        )}
        style={{
          width: `${width}px`,
          height: `${height}px`,
        }}
      >
        <div className="text-center p-2">
          <div className="text-2xl mb-1">{randomEgg.split(' ')[0]}</div>
          <div className="text-xs font-black text-purple-800 leading-tight">
            {randomEgg.split(' ').slice(1).join(' ')}
          </div>
        </div>
      </div>

      <motion.div
        drag
        dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
        whileTap={{ scale: 1.06, zIndex: 9999 }}
        whileHover={{
          scale: 1.06,
          zIndex: 9999,
          y: -6,
        }}
        whileDrag={{
          scale: 1.04,
          zIndex: 9999,
        }}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={() => setIsDragging(false)}
        initial={{ rotate: 0 }}
        animate={{ rotate: 0 }}
        style={{
          width: `${width}px`,
          height: `${height}px`,
          maxWidth: '100%',
          maxHeight: '100%',
          perspective: 400,
          transform: `rotate(0deg) rotateX(0deg) rotateY(0deg)`,
          zIndex: 2,
          WebkitTouchCallout: "none",
          WebkitUserSelect: "none",
          userSelect: "none",
          touchAction: "none",
          willChange: 'transform',
        }}
        className={cn(
          className,
          "relative mx-auto shrink-0 cursor-grab active:cursor-grabbing"
        )}
        draggable={false}
        tabIndex={0}
        aria-label={alt}
      >
        <div className="relative h-full w-full overflow-hidden rounded-3xl shadow-sm">
          <MotionImage
            className={cn("rounded-3xl object-cover")}
            fill
            src={src}
            alt={alt}
            priority={priority}
            sizes={sizes}
            {...props}
            draggable={false}
          />
        </div>
      </motion.div>

    </div>
  );
};
